import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ComposedChart,
  Area,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import html2canvas from "html2canvas";

import {
  GrowthChartAPI,
  STANDARDS_WITH_DATA,
  STANDARD_LABELS,
  type GrowthChartResponse,
  type GrowthIndicator,
  type GrowthTrendResponse,
  type Standard,
} from "../../services/growth-chart.service";
import { GuardianShareModal } from "./GuardianShareModal";

const ALL_INDICATORS: { id: GrowthIndicator; label: string }[] = [
  { id: "weight_for_age", label: "Weight-for-age" },
  { id: "height_for_age", label: "Height-for-age" },
  { id: "bmi_for_age", label: "BMI-for-age" },
  { id: "weight_for_length", label: "Weight-for-length" },
  { id: "head_circumference_for_age", label: "Head circumference-for-age" },
];

// Which indicators each standard supports - mirrors standards.py's
// StandardConfig.indicators. Used only to narrow the tab list client-side;
// the backend is the actual source of truth (a request for an unsupported
// indicator/standard combination 422s regardless).
const STANDARD_INDICATORS: Record<Standard, GrowthIndicator[]> = {
  who_2006: ["weight_for_age", "height_for_age", "bmi_for_age", "weight_for_length"],
  who_2007: ["height_for_age", "bmi_for_age"],
  cdc_2000: ["weight_for_age", "height_for_age", "bmi_for_age", "head_circumference_for_age"],
  cdc_2022_bmi: ["bmi_for_age"],
  iap_2015: ["weight_for_age", "height_for_age", "bmi_for_age"],
  fenton_2013: ["weight_for_age", "height_for_age"],
  fenton_2025: ["weight_for_age", "height_for_age"],
  intergrowth_21st: ["weight_for_age", "height_for_age", "head_circumference_for_age"],
  down_syndrome: ["weight_for_age", "height_for_age", "bmi_for_age", "head_circumference_for_age"],
};

// Explicit-override choices offered in the standard picker - only ones with
// real data loaded are selectable today (the rest would just 422), but
// listed so the picker doesn't need updating each time a standard's data
// lands - only STANDARDS_WITH_DATA needs to change.
const OVERRIDE_STANDARDS: Standard[] = [
  "who_2006", "who_2007", "cdc_2000", "cdc_2022_bmi", "iap_2015",
  "fenton_2025", "fenton_2013", "intergrowth_21st", "down_syndrome",
];

const FLAG_COLOR: Record<string, string> = {
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
  out_of_range: "#94a3b8",
};

interface GrowthChartPanelProps {
  patientId: string;
  patientName: string;
}

export const GrowthChartPanel: React.FC<GrowthChartPanelProps> = ({ patientId, patientName }) => {
  const [indicator, setIndicator] = useState<GrowthIndicator>("weight_for_age");
  const [standardOverride, setStandardOverride] = useState<Standard | "">("");
  const [chart, setChart] = useState<GrowthChartResponse | null>(null);
  const [trend, setTrend] = useState<GrowthTrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (ind: GrowthIndicator, standard: Standard | "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await GrowthChartAPI.get(patientId, ind, standard || undefined);
      setChart(data);
    } catch (err) {
      setChart(null);
      setError(err instanceof Error ? err.message : "Failed to load growth chart");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load(indicator, standardOverride);
  }, [load, indicator, standardOverride]);

  useEffect(() => {
    GrowthChartAPI.getTrend(patientId).then(setTrend).catch(() => setTrend(null));
  }, [patientId]);

  // If switching the standard override lands on one that doesn't support
  // the currently-selected indicator, fall back to the first one it does
  // support rather than repeatedly 422ing on the stale indicator.
  useEffect(() => {
    if (!chart) return;
    const supported = STANDARD_INDICATORS[chart.chart_source] ?? [];
    if (!supported.includes(indicator) && supported.length > 0) {
      setIndicator(supported[0]);
    }
  }, [chart, indicator]);

  // Narrow the indicator tabs to what the currently-plotted standard
  // actually supports (before a chart has loaded, show every indicator -
  // the first request will 422 harmlessly if the default chain picks a
  // standard that doesn't support "weight_for_age").
  const availableIndicators = chart
    ? ALL_INDICATORS.filter((i) => STANDARD_INDICATORS[chart.chart_source]?.includes(i.id))
    : ALL_INDICATORS;

  const greenPoints = (chart?.patient_series ?? []).filter((p) => p.flag === "green");
  const amberPoints = (chart?.patient_series ?? []).filter((p) => p.flag === "amber");
  const redPoints = (chart?.patient_series ?? []).filter((p) => p.flag === "red");
  const outOfRangePoints = (chart?.patient_series ?? []).filter((p) => p.flag === "out_of_range");

  // Background SD-zone shading (green -2..+2, amber -3..-2 & +2..+3, red
  // beyond) via stacked Areas - recharts has no "shade the band between two
  // curves" primitive, so each band is expressed as the delta above the one
  // below it, stacked from 0. An explicit yDomain (rather than "auto") keeps
  // the visible range anchored to the SD zones instead of 0, since a
  // zero-baseline stack would otherwise waste most of the chart's height on
  // an empty region below the reference curves.
  const curves = chart?.reference_curves ?? [];
  const seriesValues = [
    ...curves.flatMap((c) => [c.sd_neg3, c.sd_pos3]),
    ...(chart?.patient_series ?? []).map((p) => p.value),
  ];
  const minY = seriesValues.length ? Math.min(...seriesValues) : 0;
  const maxY = seriesValues.length ? Math.max(...seriesValues) : 1;
  const yPad = (maxY - minY) * 0.08 || 1;
  const yDomain: [number, number] = [Math.max(0, minY - yPad), maxY + yPad];
  const zoneData = curves.map((c) => ({
    x: c.x,
    redLow: c.sd_neg3,
    amberLow: c.sd_neg2 - c.sd_neg3,
    green: c.sd_pos2 - c.sd_neg2,
    amberHigh: c.sd_pos3 - c.sd_pos2,
    redHigh: Math.max(c.sd_pos3 - c.sd_pos2, yDomain[1] - c.sd_pos3),
  }));

  const captureChartPng = async (): Promise<string> => {
    if (!chartRef.current) throw new Error("Chart not ready");
    const canvas = await html2canvas(chartRef.current, { backgroundColor: "#ffffff", scale: 2 });
    return canvas.toDataURL("image/png").split(",")[1] ?? "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {availableIndicators.map((i) => (
            <button
              key={i.id}
              onClick={() => setIndicator(i.id)}
              className={`text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                indicator === i.id ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {i.label}
            </button>
          ))}
        </div>

        <select
          value={standardOverride}
          onChange={(e) => setStandardOverride(e.target.value as Standard | "")}
          className="text-xs sm:text-sm rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-slate-600"
          title="Override the auto-selected standard"
        >
          <option value="">Auto-select standard</option>
          {OVERRIDE_STANDARDS.map((s) => (
            <option key={s} value={s} disabled={!STANDARDS_WITH_DATA.has(s)}>
              {STANDARD_LABELS[s]}{!STANDARDS_WITH_DATA.has(s) ? " (no data yet)" : ""}
            </option>
          ))}
        </select>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading growth chart…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {chart && !loading && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">{chart.chart_label}</p>
              {chart.is_draft_reference_data && (
                <p className="text-xs text-amber-600 mt-0.5">
                  Reference data is a draft placeholder, pending clinical sign-off — not for clinical use.
                </p>
              )}
            </div>
            <button
              onClick={() => setShareOpen(true)}
              disabled={chart.patient_series.length === 0}
              className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Share with guardian
            </button>
          </div>

          {chart.patient_series.length === 0 && (
            <p className="text-xs text-slate-500">
              No measurements recorded yet — showing reference curves only.
            </p>
          )}

          <div ref={chartRef} className="bg-white rounded-xl border border-slate-100 p-2" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={["dataMin", "dataMax"]}
                  label={{ value: chart.x_unit, position: "insideBottom", offset: -2, fontSize: 11 }}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  type="number"
                  domain={yDomain}
                  label={{ value: chart.value_unit, angle: -90, position: "insideLeft", fontSize: 11 }}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area data={zoneData} dataKey="redLow" stackId="sd-zones" stroke="none" fill="#fee2e2" isAnimationActive={false} legendType="none" tooltipType="none" />
                <Area data={zoneData} dataKey="amberLow" stackId="sd-zones" stroke="none" fill="#fef3c7" isAnimationActive={false} legendType="none" tooltipType="none" />
                <Area data={zoneData} dataKey="green" stackId="sd-zones" stroke="none" fill="#dcfce7" isAnimationActive={false} legendType="none" tooltipType="none" name="normal (-2..+2 SD)" />
                <Area data={zoneData} dataKey="amberHigh" stackId="sd-zones" stroke="none" fill="#fef3c7" isAnimationActive={false} legendType="none" tooltipType="none" />
                <Area data={zoneData} dataKey="redHigh" stackId="sd-zones" stroke="none" fill="#fee2e2" isAnimationActive={false} legendType="none" tooltipType="none" />
                <Line data={chart.reference_curves} dataKey="p97" name="97th" stroke="#fca5a5" strokeWidth={1} dot={false} isAnimationActive={false} />
                <Line data={chart.reference_curves} dataKey="p85" name="85th" stroke="#fcd34d" strokeWidth={1} dot={false} isAnimationActive={false} />
                <Line data={chart.reference_curves} dataKey="p50" name="50th (median)" stroke="#64748b" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line data={chart.reference_curves} dataKey="p15" name="15th" stroke="#fcd34d" strokeWidth={1} dot={false} isAnimationActive={false} />
                <Line data={chart.reference_curves} dataKey="p3" name="3rd" stroke="#fca5a5" strokeWidth={1} dot={false} isAnimationActive={false} />
                <Scatter data={greenPoints} dataKey="value" name={`${patientName} (normal)`} fill={FLAG_COLOR.green} />
                <Scatter data={amberPoints} dataKey="value" name={`${patientName} (amber)`} fill={FLAG_COLOR.amber} />
                <Scatter data={redPoints} dataKey="value" name={`${patientName} (red)`} fill={FLAG_COLOR.red} />
                <Scatter data={outOfRangePoints} dataKey="value" name={`${patientName} (out of range)`} fill={FLAG_COLOR.out_of_range} shape="triangle" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {outOfRangePoints.length > 0 && (
            <p className="text-xs text-slate-500">
              {outOfRangePoints.length} measurement{outOfRangePoints.length > 1 ? "s" : ""} fell outside{" "}
              {chart.chart_label}'s valid range for this indicator and {outOfRangePoints.length > 1 ? "aren't" : "isn't"} scored
              (shown as grey triangles) rather than given a potentially wrong flag.
            </p>
          )}

          {trend && trend.velocity.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Growth velocity across visits</p>
              <div className="space-y-1">
                {trend.velocity.map((v, idx) => (
                  <div key={idx} className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                    <span>{new Date(v.period_start).toLocaleDateString()} → {new Date(v.period_end).toLocaleDateString()}</span>
                    {v.weight_velocity_kg_per_month != null && (
                      <span>weight: {v.weight_velocity_kg_per_month.toFixed(2)} kg/mo</span>
                    )}
                    {v.height_velocity_cm_per_month != null && (
                      <span>height: {v.height_velocity_cm_per_month.toFixed(2)} cm/mo</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {shareOpen && chart && (
        <GuardianShareModal
          patientId={patientId}
          patientName={patientName}
          indicator={indicator}
          chart={chart}
          onCapturePng={captureChartPng}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};
